import { AloftPrice } from '@aloft/toolkit-shared';

import { Typography } from '../../../summary/components/Typography.tsx';
import { isCosted } from '../AdjustmentBreakdown/isCosted.ts';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import {
  BreakdownColumn,
  CombinedColumn,
  SingleColumn,
  Tkv2PairedCostSummaryInterfaceV2,
} from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { percent } from '../utils/formatters/percent.ts';
import { wholeDollar } from '../utils/formatters/wholeDollar.ts';
import { getTableData } from '../utils/getTableData.ts';
import { hasAnyValues } from '../utils/hasAnyValues.ts';
import { hasNoStructuresButOtherCompsDo } from '../utils/hasNoStructuresButOtherCompsDo.ts';

const getColumns = (
  pathPrefix: string,
): (SingleColumn<'feature' | 'adjustment'> | CombinedColumn)[] => [
  {
    header: 'SF',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: `${pathPrefix}.sqft`,
  },
  {
    header: 'QL',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: `${pathPrefix}.dcQuality`,
  },
  {
    header: 'Cost New',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: `${pathPrefix}.costNew`,
    valueFormatter: wholeDollar,
  },
  {
    header: '- Dep',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: `${pathPrefix}.depreciationRatio`,
    valueFormatter: percent,
  },
  {
    header: '= Dep Cost',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: `${pathPrefix}.depreciatedCost`,
    valueFormatter: wholeDollar,
  },
];

const tableDef: BreakdownColumn[] = [
  {
    subRows: [
      {
        header: 'Feature',
        label: 'None',
        columns: getColumns(''),
        shouldShowForProperty: hasNoStructuresButOtherCompsDo(getColumns, [
          'coveredPatioOrGazebo',
          'deck',
          'enclosedPatio',
        ]),
      },
      {
        header: 'Feature',
        label: 'Covered Patio or Gazebo',
        columns: getColumns('coveredPatioOrGazebo'),
        shouldShowForProperty: hasAnyValues(getColumns, 'coveredPatioOrGazebo'),
      },
      {
        header: 'Feature',
        label: 'Deck',
        columns: getColumns('deck'),
        shouldShowForProperty: hasAnyValues(getColumns, 'deck'),
      },
      {
        header: 'Feature',
        label: 'Enclosed Patio',
        columns: getColumns('enclosedPatio'),
        shouldShowForProperty: hasAnyValues(getColumns, 'enclosedPatio'),
      },
    ],
  },
  {
    header: 'Total Dep Cost',
    type: 'feature',
    adjustmentType: 'porchPatioDeck',
    path: 'totalDepreciatedCost',
    valueFormatter: wholeDollar,
    typographyVariant: 'h5',
  },
  {
    header: 'Adj',
    type: 'adjustment',
    adjustmentType: 'porchPatioDeck',
    path: 'adjustment',
    valueFormatter: adjustment,
  },
];

export const AdjustmentBreakdownDepreciatedCostAdditionalImprovementsPatioDeck = (
  props: Tkv2PairedCostSummaryInterfaceV2,
) => {
  const { compSequence, results } = props;
  if (!isCosted(results, 'porchPatioDeck')) return null;

  const tableData = getTableData(tableDef, props, compSequence);

  const sortedPrices = Object.values(tableData)
    .flatMap((row) => row[0])
    .filter(Array.isArray)
    .map((subRow) => subRow[5])
    .filter(AloftPrice.isAloftPrice)
    .sort((a, b) => a.compare(b));

  const minString = sortedPrices[0]?.toString(true);
  const maxString = sortedPrices[sortedPrices.length - 1]?.toString(true);
  const rangeString = [minString, maxString].join(' to ');

  return (
    <SummarySection marginBottom="2rem">
      <TitleAndExportToggleLockup
        {...props}
        title="Porch/Patio/Deck"
        id="isDepreciatedCostAdditionalImprovementsPorchPatioDeckInExport"
        variant="h3"
      />
      <Typography variant="body1">
        {`Porch adjustments are based on the difference in depreciated cost between the subject and comparable.  Values ranged from ${rangeString}.`}
      </Typography>
      <SummaryTableHorizontal
        {...props}
        summary={props}
        compSequence={compSequence}
        columns={tableDef}
        tableData={tableData}
        subjectHeader="SUB"
        compHeaderPrefix="CP"
      />
    </SummarySection>
  );
};
